import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Checkbox, FormControlLabel, Paper, TextField, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { LeftMenuDrawer } from '../../navigation/LeftMenuDrawer/LeftMenuDrawer';
import { SearchMenuBar } from '../../navigation/SearchMenuBar';
import { useRestaurantDirectory } from '../../../hooks/useRestaurantDirectory';
import { canViewRestaurantDirectory, normalizePrepTime, parsePrepThreshold, selectDirectoryRestaurants } from '../../../services/RestaurantDirectory';
import { useStyles } from './RestaurantDirectoryPage.styles';

function text(value) {
  return value == null ? '' : String(value).trim();
}

function errorMessage(error) {
  const status = Number(error?.response?.status ?? error?.status ?? error?.statusCode);
  if (status === 401) {
    return 'Session expired. Sign in again.';
  }
  if (status === 403) {
    return 'No directory permission. Contact an administrator.';
  }
  return 'Refresh failed. Stale records hidden.';
}

function DirectoryContent() {
  const classes = useStyles();
  const { data, isFetching, error, refetch } = useRestaurantDirectory();
  const [closedOnly, setClosedOnly] = useState(true);
  const [highOnly, setHighOnly] = useState(true);
  const [thresholdInput, setThresholdInput] = useState('35');
  const [sort, setSort] = useState('name');
  const threshold = parsePrepThreshold(thresholdInput);
  const invalidThreshold = highOnly && threshold == null;
  const ready = !isFetching && !error && Array.isArray(data);
  const matches = ready ? selectDirectoryRestaurants(data, { closedOnly, highOnly, threshold, sort }) : [];
  const reset = () => {
    setClosedOnly(true);
    setHighOnly(true);
    setThresholdInput('35');
    setSort('name');
  };

  const refresh = () => {
    const request = refetch();
    if (request && typeof request.catch === 'function') request.catch(() => {});
  };

  return (
    <>
      <Box className={classes.introduction}>
        <Typography variant="h5" component="h1" className={classes.title}>Restaurant directory</Typography>
      </Box>
      <Paper className={classes.panel}>
        <Box className={classes.toolbar}>
          <Typography variant="body2" component="h2" aria-live="polite">
            {ready ? `${matches.length} / ${data.filter((row) => row.isActive === true).length} restaurants` : 'Restaurants'}
          </Typography>
          <Button variant="outlined" color="primary" onClick={refresh} disabled={isFetching}>
            Refresh
          </Button>
        </Box>
        <Box className={classes.filters} role="group" aria-label="Restaurant filters and ordering">
          <FormControlLabel control={<Checkbox color="primary" checked={closedOnly}
            onChange={(event) => setClosedOnly(event.target.checked)} />} label="Temp closed" />
          <FormControlLabel control={<Checkbox color="primary" checked={highOnly}
            onChange={(event) => setHighOnly(event.target.checked)} />} label="High prep" />
          <TextField id="prep-threshold" label="Prep ≥ (min)" variant="outlined" size="small"
            value={thresholdInput} onChange={(event) => setThresholdInput(event.target.value)}
            inputProps={{ inputMode: 'numeric' }} error={invalidThreshold}
            FormHelperTextProps={{ role: invalidThreshold ? 'alert' : undefined }}
            helperText={invalidThreshold ? 'Enter positive whole minutes.' : undefined} />
          <TextField id="restaurant-sort" select label="Sort" variant="outlined" size="small"
            value={sort} onChange={(event) => setSort(event.target.value)} SelectProps={{ native: true }}>
            <option value="name">Name</option>
            <option value="highest">Prep: highest</option>
            <option value="lowest">Prep: lowest</option>
          </TextField>
          <Button onClick={reset} color="primary">Reset</Button>
        </Box>
        <Box aria-busy={Boolean(isFetching)}>
          {isFetching && <Box role="status" aria-live="polite" className={classes.state}>
            <Box aria-hidden="true">
              {[0, 1, 2, 3].map((index) => <Skeleton key={index} height={56} />)}
            </Box>
            <Typography variant="body2">Loading…</Typography>
          </Box>}
          {!isFetching && error && <Box role="alert" className={classes.state}>
            <Typography variant="h6" component="h3" className={classes.stateTitle}>Directory unavailable</Typography>
            <Typography variant="body2">{errorMessage(error)}</Typography>
            <Button variant="outlined" color="primary" onClick={refresh}>Retry</Button>
          </Box>}
          {!isFetching && !error && !Array.isArray(data) && <Box role="status" className={classes.state}>
            <Typography variant="h6" component="h3" className={classes.stateTitle}>Directory not loaded</Typography>
            <Button variant="outlined" color="primary" onClick={refresh}>Retry</Button>
          </Box>}
          {ready && data.length === 0 && <Box role="status" className={classes.state}>
            <Typography variant="h6" component="h3" className={classes.stateTitle}>No restaurants to display</Typography>
          </Box>}
          {ready && data.length > 0 && matches.length === 0 &&
            <Box role="status" className={classes.state}>
              <Typography variant="h6" component="h3">No matching restaurants</Typography>
            </Box>}
          {ready && matches.length > 0 && <table className={classes.table} aria-label="Restaurant pickup information">
            <thead><tr>
               {['Restaurant', 'Configured prep', 'Closure', 'Phone', 'Pickup address & instructions'].map((heading) =>
                <th key={heading} scope="col">{heading}</th>)}
            </tr></thead>
            <tbody>{matches.map((restaurant) => {
              const closed = restaurant.tempClosed === true;
              const closureStatus = closed ? 'Temporarily closed' :
                restaurant.tempClosed === false ? 'Not temporarily closed' : 'Status unavailable';
              const street = text(restaurant.address);
              const locality = [text(restaurant.city), text(restaurant.state)].filter(Boolean).join(', ');
              const localityAndZip = [locality, text(restaurant.zip)].filter(Boolean).join(' ');
              const addressLines = [street, localityAndZip].filter(Boolean);
              const note = text(restaurant.driverNote);
              const reason = text(restaurant.tempClosedReason);
              const prep = normalizePrepTime(restaurant.prepTime);
              const prepAvailable = prep != null;
              const highPrep = prepAvailable && threshold != null && prep >= threshold;

              return <tr key={restaurant.id}>
                <td data-label="Restaurant">
                  <span className={classes.name}>{text(restaurant.name) || 'Name unavailable'}</span>
                </td>
                <td data-label="Configured prep time" className={highPrep ? classes.highPrep : undefined}>
                  {prepAvailable ? `${prep} minutes` : 'Prep time unavailable'}
                  {highPrep && <span className={classes.highPrepLabel}>High prep time</span>}
                </td>
                <td data-label="Temporary closure">
                  <span className={`${classes.status} ${closed ? classes.closed : ''}`}>{closureStatus}</span>
                  {closed && reason && <Typography variant="body2" className={`${classes.reason} ${classes.multiline}`}>
                    Reason: {reason}
                  </Typography>}
                </td>
                <td data-label="Phone">{text(restaurant.phone) || 'Phone unavailable'}</td>
                <td data-label="Pickup address & instructions">
                  {addressLines.length ? addressLines.map((line, index) =>
                    <div key={index} className={classes.multiline}>{line}</div>) : 'Address unavailable'}
                  {note && <Box className={classes.note}>
                    <Typography variant="caption" component="span" className={classes.noteLabel}>Driver note</Typography>
                    <Typography variant="body2" className={classes.multiline}>{note}</Typography>
                  </Box>}
                </td>
              </tr>;
            })}</tbody>
          </table>}
        </Box>
      </Paper>
    </>
  );
}

export function RestaurantDirectoryPage() {
  const classes = useStyles();
  const user = useSelector((state) => state.user.user);

  return (
    <main className={classes.content} aria-label="Restaurant directory page">
      <LeftMenuDrawer />
      <Box className={classes.header}><SearchMenuBar title="Restaurant Directory" /></Box>
      <Box className={classes.body}>
        {user?.id != null && canViewRestaurantDirectory(user) ? <DirectoryContent key={user.id} /> :
          <Paper className={classes.panel}>
            <Typography variant="h5" component="h1" className={classes.title}>Restaurant directory access required</Typography>
            <Typography role="alert" variant="body2">
              {user?.id == null ? 'Sign in with an authorized account to view restaurant pickup information.' :
                'Your account does not have permission to view restaurant pickup information. Contact an administrator if you need access.'}
            </Typography>
          </Paper>}
      </Box>
    </main>
  );
}
